import { HttpClientModule } from "@angular/common/http";
import { RouterLink, RouterOutlet } from "@angular/router";
import {
  AfterViewInit,
  Component,
  OnInit,
  Signal,
  computed,
} from "@angular/core";

import {
  BrandingService,
  BreadcrumbsComponent,
  IconModule,
} from "@yoimo/joymo-ui";
import { EMPTY, catchError, switchMap } from "rxjs";

import { isValidEmail } from "./utils";
import { APP_ROUTES } from "./app.routes";
import { CoverImageComponent } from "./layout/cover-image/cover-image.component";
import { FooterComponent } from "./layout/footer/footer.component";
import { HeaderComponent } from "./layout/header/header.component";
import {
  AppStateService,
  AppWorkflowService,
  AuthService,
  LoggingService,
  RedirectionService,
} from "@app/services";
import { NgStyle } from "@angular/common";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    CoverImageComponent,
    FooterComponent,
    HeaderComponent,
    HttpClientModule,
    IconModule,
    NgStyle,
    RouterLink,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit {
  readonly homepageUrl: Signal<string>;

  constructor(
    private authService: AuthService,
    private appState: AppStateService,
    private redirectionService: RedirectionService,
    private workflowService: AppWorkflowService
  ) {
    this.homepageUrl = computed(() => {
      const workflow = this.workflowService.workflow();
      return APP_ROUTES[workflow!] || "";
    });
  }

  async ngOnInit(): Promise<void> {
    BrandingService.applyCssConstants();
    await this.authService.logOut();
    this.authService.auth.onAuthStateChanged((user) => {
      if (!user) return;
      if (!isValidEmail(user.email)) {
        this.appState.setError("joymo-auth/missing-email-permission");
        user.delete();
        return;
      }

      // User is logged in, emit it:
      LoggingService.linkToUser(user);
      this.authService.setUser(user);
    });

    this.authService
      .getUser$(true)
      .pipe(
        switchMap(() => this.redirectionService.goToRedirectUrl()),
        catchError((err: Error) => {
          this.appState.setError(err.message);
          return EMPTY;
        })
      )
      .subscribe();
  }

  canNavigateBack(): boolean {
    return [/login\/with-email/, /signup\/with-email/, /forgot-password$/].some(
      (p) => location.pathname.match(p)
    );
  }
}
